import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApiTypes,
  CollectionUpdateErrorEvent,
  EntitlementUpdateErrorEvent,
  CollectionAccessLevel,
} from '@mode/shared/contract-common';
import { SpaceDomain } from '@mode/shared/contract-common';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ApiService } from '@mode/shared/util-halogen';
import { getEmbeddedCollection } from '@mode/shared/util-halogen';
import {
  toCollectionsWithPaginatorAndQuery,
  toEntitlement,
  toPermissionsSearchResult,
  toSpace,
} from '../data-mappers/space-data-mappers';
import { spaceCache } from '../resource-cache';
import { SpaceEntitlementResource, SpacePermissionsSearchResultResource, SpaceResource } from './spaces-response.types';
import { AccountFetchService } from '../accounts/account-fetch.service';

/**
 * @todo (Franklin) 03/04/2021: Refactor to use `ApiService` https://github.com/mode/issues/issues/7417
 */
@Injectable({
  providedIn: 'root',
})
export class SpacesClientService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private accountFetcher: AccountFetchService
  ) {}

  private getSpaceResource(accountId: string, spaceToken: string) {
    const url = `/api/${accountId}/spaces/${spaceToken}`;
    const cached = spaceCache.get(spaceToken);

    if (cached == null) {
      return from(this.apiService.getFromPath<SpaceResource>(url)).pipe(
        tap((resource) => spaceCache.set(resource.token, resource))
      );
    } else {
      return of(cached);
    }
  }

  public getSpace(accountId: string, spaceToken: string) {
    return this.getSpaceResource(accountId, spaceToken).pipe(map((resource) => toSpace(resource)));
  }

  public createSpaceEntitlement(
    accountId: string,
    spaceToken: string,
    entitlementType: string,
    memberToken: string,
    memberType: 'UserGroup' | 'User'
  ) {
    const url = `/api/${accountId}/spaces/${spaceToken}/permissions`;
    const body = {
      permission: {
        resource_type: 'space',
        resource_token: spaceToken,
        action: entitlementType,
        accessor_type: memberType,
        accessor_token: memberToken,
      },
    };

    return this.http.post<SpaceEntitlementResource>(url, body).pipe(
      map((response: SpaceEntitlementResource): SpaceDomain.Entitlement => toEntitlement(response)),
      catchError((err) => {
        console.error('Error creating entitlement', err);
        return throwError(err); // rethrow error
      })
    );
  }

  public deleteAllSpaceEntitlements(accountId: string, spaceToken: string) {
    return this.getSpaceResource(accountId, spaceToken).pipe(
      switchMap((resource) => {
        return this.apiService.destroy<SpaceResource>(resource, 'destroy_entitlements');
      })
    );
  }

  public deleteSpaceEntitlement(accountId: string, spaceToken: string, entitlementToken: string) {
    const url = `/api/${accountId}/spaces/${spaceToken}/permissions/${entitlementToken}`;

    return this.http.delete<SpaceEntitlementResource>(url);
  }

  /**
   * Retrieve list of entitlements for a given space
   */
  public getEntitlements(accountId: string, spaceToken: string) {
    const url = `/api/${accountId}/spaces/${spaceToken}/permissions?embed[entitlements][accessor]=1`;

    return this.http.get<ApiTypes.ModeHalResource>(url).pipe(
      map((response: ApiTypes.ModeHalResource): SpaceDomain.Entitlement[] => {
        const entitlements = getEmbeddedCollection<SpaceEntitlementResource>(response, 'entitlements');
        const mapData = (entitlementResource: SpaceEntitlementResource) => toEntitlement(entitlementResource);
        return (entitlements || []).map(mapData);
      }),
      catchError((err) => {
        console.error('Error searching for entitled members', err);
        return throwError(err); // rethrow error
      })
    );
  }

  /**
   * Search for members of the space that do not have entitlements. Members
   * include Users and UserGroups
   */
  public searchUnentitledSpaceMembers(accountId: string, spaceToken: string, query: string) {
    const url = `/api/${accountId}/spaces/${spaceToken}/permissions/search`;
    const params = new HttpParams().set('query', query);

    return this.http.get<SpacePermissionsSearchResultResource>(url, { params }).pipe(
      map((response: ApiTypes.ModeHalResource): SpaceDomain.PermissionsSearchResult[] => {
        const results = getEmbeddedCollection<SpacePermissionsSearchResultResource>(response, 'results');
        const mapData = (resultResource: SpacePermissionsSearchResultResource) =>
          toPermissionsSearchResult(resultResource);

        return (results || []).map(mapData);
      }),
      catchError((err) => {
        console.error('Error searching for unentitled members', err);
        return throwError(err); // rethrow error
      })
    );
  }

  public updateSpaceEntitlement(
    accountId: string,
    spaceToken: string,
    entitlementToken: string,
    entitlementType: string
  ) {
    const url = `/api/${accountId}/spaces/${spaceToken}/permissions/${entitlementToken}?embed[accessor]=1`;
    return this.http
      .patch<SpaceEntitlementResource>(url, {
        permission: { action: entitlementType },
      })
      .pipe(
        map((response) => toEntitlement(response)),
        catchError((error: HttpErrorResponse) => {
          const errorEvent: EntitlementUpdateErrorEvent = { entitlementToken, error };

          console.error('Error updating entitlement', error);
          return throwError(errorEvent); // rethrow error
        })
      );
  }

  getMoveableCollections(workspaceName: string) {
    return this.accountFetcher.getAccountResource(workspaceName).pipe(
      switchMap((workspaceResource) => {
        return this.apiService.getResources<SpaceResource>(workspaceResource, 'move_to_spaces', {
          relationAs: 'spaces',
        });
      }),
      map((resources) => resources.map((r) => toSpace(r)))
    );
  }

  public updateDefaultAccessLevel(
    workspaceId: string,
    collectionToken: string,
    defaultAccessLevel: CollectionAccessLevel
  ): Observable<SpaceDomain.Space> {
    const url = `/api/${workspaceId}/spaces/${collectionToken}`;

    return this.http
      .patch<SpaceResource>(url, {
        space: {
          default_access_level: defaultAccessLevel,
        },
      })
      .pipe(
        tap((collectionResource) => {
          // update the collection in the cache
          spaceCache.set(collectionToken, collectionResource);
        }),
        map((resource) => toSpace(resource)),
        catchError((error: HttpErrorResponse) => {
          const errorEvent: CollectionUpdateErrorEvent = { collectionToken, error };

          console.error('Error updating default access level', error);
          return throwError(errorEvent); // rethrow error
        })
      );
  }

  public searchCollections(
    workspaceId: string,
    pageNumber = 1,
    query = ''
  ): Observable<SpaceDomain.CollectionsSearchResult> {
    const url = `/api/${workspaceId}/collections?filter=custom&embed[spaces][creator]=1&embed[spaces][detail]=1&page=${pageNumber}&search=${query}`;

    return this.http.get<ApiTypes.ModeHalResource>(url).pipe(
      map((resource: ApiTypes.ModeHalResource) => toCollectionsWithPaginatorAndQuery(resource)),
      catchError((err) => {
        console.error('Error fetching collections by pagination and query', err);
        return throwError(err);
      })
    );
  }
}
